import dayjs from 'dayjs';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';

export class CustomerSubscription extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '61dd590312de4a5baeed041679d1cb37';
  static readonly SCHEMA_VERSION: number = 4;

  constructor(
        public readonly uniqueId: string,
        public readonly businessRef: BusinessModuleRef,
        public readonly customerId: string,
        public readonly creationDate: dayjs.Dayjs,
        public isBlacklisted: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return CustomerSubscription.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CustomerSubscription.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CustomerSubscription {
    return new CustomerSubscription(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'customerId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<boolean>(map, 'isBlacklisted'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'customerId': this.customerId,
      'creationDate': context.encodeDate(this.creationDate),
      'isBlacklisted': this.isBlacklisted,
    };
  }

  clone(): this {
    return new CustomerSubscription(
        this.uniqueId,
        this.businessRef.clone(),
        this.customerId,
        TimeZone.cloneDate(this.creationDate),
        this.isBlacklisted,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        other.customerId === this.customerId &&
        other.creationDate === this.creationDate &&
        other.isBlacklisted === this.isBlacklisted;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new CustomerSubscription(
        this.uniqueId,
        businessRef.clone(),
        this.customerId,
        TimeZone.cloneDate(this.creationDate),
        this.isBlacklisted,
    ) as this;
  }

  copyWith(fields?: {
      uniqueId?: string,
      customerId?: string,
      creationDate?: dayjs.Dayjs,
      isBlacklisted?: boolean,
  }): CustomerSubscription {
    return new CustomerSubscription(
        fields?.uniqueId ?? this.uniqueId,
        this.businessRef.clone(),
        fields?.customerId ?? this.customerId,
        fields?.creationDate ?? TimeZone.cloneDate(this.creationDate),
        fields?.isBlacklisted ?? this.isBlacklisted,
    );
  }
}

export class CustomerSubscriptionBuilder extends DataModelBuilder<CustomerSubscription> {
  build(context: SerializationContext, map: SerializedData): CustomerSubscription {
    return CustomerSubscription.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CustomerSubscription.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(CustomerSubscription.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [businessId] was replaced with [businessRef]
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 2;
    }

    // / Version 3 introduces the [isBlacklisted] flag.
    if(version < 3) {
      map['isBlacklisted'] ??= false;
      version = 3;
    }

    // In this version [clientId] was renamed to [customerId]
    if(version < 4) {
      map['customerId'] = map['clientId'];
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
